import { DCP_CHECK_STATUSES, type DcpCheckStatus } from '@/lib/db-tenant'

/**
 * MultiSelect value of the explicit "All" option of the Status quick filter.
 * UI-only: it never leaves this module's mapping — the page state and the
 * query only ever see real statuses, with an empty selection meaning "all".
 */
export const ALL_STATUSES_VALUE = 'all'

/**
 * Narrow a MultiSelect value (plain string) to a known check status. Drops the
 * {@link ALL_STATUSES_VALUE} sentinel and anything else unexpected.
 *
 * @param value - One selected option value.
 * @returns Whether it is a {@link DcpCheckStatus}.
 */
export function isDcpCheckStatus(value: string): value is DcpCheckStatus {
  return (DCP_CHECK_STATUSES as readonly string[]).includes(value)
}

/**
 * MultiSelect value for a status selection: "All" checked alone when nothing
 * specific is selected, otherwise exactly the selected statuses.
 *
 * @param statuses - Selected statuses; empty means all.
 * @returns The `value` to pass to the MultiSelect.
 */
export function toStatusSelectValue(statuses: readonly DcpCheckStatus[]): string[] {
  return statuses.length === 0 ? [ALL_STATUSES_VALUE] : [...statuses]
}

/**
 * Next status selection after the MultiSelect reports a change.
 *
 * saas-ui `MultiSelect` emits the whole next array (a toggled value appended
 * or filtered out, `[]` from its clear button), so the clicked option is
 * inferred as whatever is in `next` but was not shown before — compared as
 * sets, never by position. Rules:
 *  - "All" newly checked → back to all (clears specific statuses);
 *  - otherwise the specific statuses in `next` win, which drops "All" when a
 *    status is picked while it was checked;
 *  - nothing specific left (last status unchecked, "All" unchecked while
 *    alone, the clear button) → all; the filter is never empty and unlabelled.
 *
 * @param prev - The current selection (empty = all).
 * @param next - The array the MultiSelect emitted.
 * @returns The new selection in the fixed issued → paid → voided order; empty = all.
 */
export function nextStatusSelection(
  prev: readonly DcpCheckStatus[],
  next: readonly string[],
): DcpCheckStatus[] {
  const shown = new Set(toStatusSelectValue(prev))
  const allJustChecked = next.some((value) => value === ALL_STATUSES_VALUE && !shown.has(value))
  if (allJustChecked) return []

  const picked = new Set(next.filter(isDcpCheckStatus))
  return DCP_CHECK_STATUSES.filter((status) => picked.has(status))
}
